/**
 * ComposeSenderInput Component
 * Editable sender section for guest users
 */

import { cn } from '@/lib/utils';
import { SPACING, TYPOGRAPHY } from '@/design-system';
import { RESPONSIVE } from '@/design-system/helpers/responsive';
import { User } from 'lucide-react';
import { EmailInput } from '@/components/auth/EmailInput';
import { NameInput } from '@/components/auth/NameInput';
import { LCommon, LComponents } from '@/components/i18n';

interface ComposeSenderInputProps {
  guestEmail: string;
  guestName: string;
  emailError?: string;
  nameError?: string;
  onEmailChange: (email: string) => void;
  onNameChange: (name: string) => void;
  onEmailBlur?: () => void;
  onNameBlur?: () => void;
  disabled?: boolean;
}

export function ComposeSenderInput({
  guestEmail,
  guestName,
  emailError,
  nameError,
  onEmailChange,
  onNameChange,
  onEmailBlur,
  onNameBlur,
  disabled = false,
}: ComposeSenderInputProps) {
  return (
    <div className={cn('bg-muted/30 rounded-lg p-4 border border-border/50', SPACING.stackMd)}>
      <div className={RESPONSIVE.flexRowCenterGap3}>
        <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
          <User className="w-5 h-5 text-muted-foreground" />
        </div>
        <div className="flex-1 min-w-0">
          <p className={cn(TYPOGRAPHY.cardTitleSmall, 'mb-1')}>
            <LComponents id="compose_message.guest.your_info" defaultValue="Your Contact Information" />
          </p>
          <p className={TYPOGRAPHY.caption}>
            <LComponents id="compose_message.guest.info_description" defaultValue="Enter your details so experts can respond to you" />
          </p>
        </div>
      </div>
      
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <EmailInput
          id="guest-email"
          value={guestEmail}
          onChange={onEmailChange}
          onBlur={onEmailBlur}
          error={emailError}
          disabled={disabled}
          required
          compact
        />
        <NameInput
          id="guest-name"
          value={guestName}
          onChange={onNameChange}
          onBlur={onNameBlur}
          error={nameError}
          disabled={disabled}
          required={false}
          compact
        />
      </div>
    </div>
  );
}
